import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const field = "bg-zinc-900/70 border-zinc-800 text-sm";

export default function Settings() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role !== "member";
  const [org, setOrg] = useState(null);
  const [team, setTeam] = useState([]);
  const [audit, setAudit] = useState([]);
  const [billingState, setBillingState] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", password: "", role: "member" });

  const load = async () => {
    const [o, t] = await Promise.all([api.get("/org"), api.get("/auth/team")]);
    setOrg(o.data);
    setTeam(t.data);
    api.get("/billing/subscription").then((r) => setBillingState(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/audit-log").then((r) => setAudit(r.data)).catch(() => {});
      api.get("/billing/payments").then((r) => setPayments(r.data)).catch(() => {});
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveOrg = async (payload) => {
    try {
      const { data } = await api.put("/org", payload);
      setOrg(data);
      await refresh();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addMember = async () => {
    try {
      await api.post("/auth/team", invite);
      setShowInvite(false);
      setInvite({ name: "", email: "", password: "", role: "member" });
      load();
      toast.success("Team member added");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/auth/team/${id}`);
      load();
      toast.success("Member removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!org)
    return (
      <AppShell title="Settings">
        <div className="h-40 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </AppShell>
    );

  return (
    <AppShell title="Settings" subtitle="Organisation, appearance, team and audit trail">
      <div className="grid lg:grid-cols-2 gap-5">
        <div data-testid="billing-card" className="nv-card p-5 space-y-3 lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-base font-semibold">Subscription</h3>
            {billingState && (
              <span className={`font-num uppercase text-[10px] px-2 py-0.5 rounded-full border ${billingState.read_only ? "border-red-500/30 text-red-300" : "border-emerald-500/30 text-emerald-300"}`}>
                {billingState.status}
              </span>
            )}
            <Button data-testid="manage-plan-btn" size="sm" onClick={() => navigate("/plans")} className="ml-auto h-8 bg-indigo-500 hover:bg-indigo-600 text-white">
              {billingState?.is_trial ? "Choose a plan" : "Change plan"}
            </Button>
          </div>
          {billingState && (
            <>
              <div className="grid sm:grid-cols-4 gap-3 text-sm">
                <div>
                  <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Plan</p>
                  <p className="mt-1 text-zinc-100">{billingState.plan_label}</p>
                </div>
                <div>
                  <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Proposals used</p>
                  <p className="mt-1 font-num text-zinc-100">
                    {billingState.proposals_used}/{billingState.proposal_limit}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Seats</p>
                  <p className="mt-1 font-num text-zinc-100">
                    {billingState.seats_used}/{billingState.seats}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Renews</p>
                  <p className="mt-1 font-num text-zinc-100">
                    {billingState.period_end ? new Date(billingState.period_end).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
              <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full bg-indigo-500 transition-all duration-700"
                  style={{ width: `${Math.min(100, (billingState.proposals_used / Math.max(1, billingState.proposal_limit)) * 100)}%` }}
                />
              </div>
              {billingState.read_only && (
                <p className="text-xs text-red-300">
                  Your workspace is read-only until billing is settled. Existing proposals stay accessible.
                </p>
              )}
              {payments.length > 0 && (
                <div className="pt-3 border-t border-zinc-800/70 space-y-1.5">
                  <p className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Payment history</p>
                  {payments.slice(0, 5).map((p) => (
                    <div key={p.id} className="flex justify-between text-xs font-num text-zinc-400">
                      <span>{p.plan}</span>
                      <span>₹{p.amount_inr.toLocaleString("en-IN")}</span>
                      <span className={p.status === "paid" ? "text-emerald-400" : "text-zinc-500"}>{p.status}</span>
                      <span className="text-zinc-600">{p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="nv-card p-5 space-y-3">
          <h3 className="text-base font-semibold">Organisation</h3>
          <div>
            <label className="text-xs text-zinc-500">Name</label>
            <Input data-testid="org-name-input" className={`mt-1 ${field}`} value={org.name} onChange={(e) => setOrg({ ...org, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-zinc-500">Default currency</label>
              <select data-testid="org-currency-select" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={org.currency} onChange={(e) => setOrg({ ...org, currency: e.target.value })}>
                {["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-zinc-500">GST mode</label>
              <select data-testid="client-gst-toggle" className={`mt-1 h-9 w-full rounded-md px-2 ${field}`} value={org.gst_mode} onChange={(e) => setOrg({ ...org, gst_mode: e.target.value })}>
                <option value="none">No GST</option>
                <option value="cgst_sgst">CGST + SGST</option>
                <option value="igst">IGST</option>
                <option value="single">Single rate</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-500">GSTIN (shown on documents)</label>
            <Input data-testid="org-gstin-input" className={`mt-1 ${field} font-num`} value={org.gstin || ""} onChange={(e) => setOrg({ ...org, gstin: e.target.value })} />
          </div>
          <Button
            data-testid="save-org-btn"
            disabled={!isAdmin}
            onClick={() => saveOrg({ name: org.name, currency: org.currency, gst_mode: org.gst_mode, gstin: org.gstin || "" })}
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
          >
            Save organisation
          </Button>
          <div className="pt-3 border-t border-zinc-800/70 text-xs font-num text-zinc-500 flex gap-4">
            <span>Plan: {org.plan}</span>
            <span>Seats: {org.seats_used}/{org.seats}</span>
            <span>Proposal credits: {org.proposal_limit}</span>
          </div>
        </div>

        <div className="nv-card p-5 space-y-3" data-testid="appearance-card">
          <h3 className="text-base font-semibold">Appearance</h3>
          <p className="text-xs text-zinc-500">
            Choose how Propojo looks on this device. System follows your operating system setting.
          </p>
          <ThemeToggle withLabels className="flex-wrap" />
        </div>

        <div className="nv-card p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold">Team</h3>
            {isAdmin && (
              <Button data-testid="add-member-btn" size="sm" variant="outline" onClick={() => setShowInvite(true)} className="h-8 border-zinc-800 text-zinc-300">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="mt-3 divide-y divide-zinc-800/60">
            {team.map((m) => (
              <div key={m.id} data-testid={`team-row-${m.id}`} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-zinc-200 truncate">{m.name}</p>
                  <p className="text-xs text-zinc-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-num uppercase tracking-wide px-2 py-0.5 rounded-full border border-zinc-800 text-zinc-400">{m.role}</span>
                  {isAdmin && m.role !== "owner" && (
                    <button data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m.id)} className="p-1.5 rounded hover:bg-red-500/15">
                      <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="nv-card p-5">
            <h3 className="text-base font-semibold">Audit log</h3>
            <p className="text-xs text-zinc-500 mb-3">Sensitive and financial actions</p>
            <div className="max-h-72 overflow-y-auto nv-scroll divide-y divide-zinc-800/60">
              {!audit.length && <p className="text-xs text-zinc-600 py-3">Nothing logged yet.</p>}
              {audit.map((a) => (
                <div key={a.id} className="py-2 flex justify-between gap-3 text-xs">
                  <span className="font-num text-indigo-300">{a.action}</span>
                  <span className="text-zinc-500 truncate">{a.target}</span>
                  <span className="text-zinc-600 font-num shrink-0">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="bg-panel border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle>Add team member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="invite-name-input" className={field} placeholder="Full name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            <Input data-testid="invite-email-input" className={field} placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <Input data-testid="invite-password-input" className={field} type="password" placeholder="Temporary password (min 8)" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} />
            <select data-testid="invite-role-select" className={`h-9 w-full rounded-md px-2 ${field}`} value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            <Button data-testid="save-member-btn" onClick={addMember} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
              Add member
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
